import ReactCountryFlag from "react-country-flag";
import { countryCodeToName } from "../../utils/countries";
import windowsIcon from "../../assets/windows.svg";
import macIcon from "../../assets/mac.svg";
import androidIcon from "../../assets/android.svg";
import iosIcon from "../../assets/ios.svg";
import chromeIcon from "../../assets/chrome.svg";
import safariIcon from "../../assets/safari.svg";
import firefoxIcon from "../../assets/firefox.svg";
import edgeIcon from "../../assets/edge.svg";

export function adminLocationCoord(value) {
  if (value == null || value === "") return NaN;
  const n = Number(value);
  return Number.isFinite(n) ? n : NaN;
}

export function adminDisplayableIp(value) {
  if (value == null || value === "") return "";
  const s = String(value).trim().replace(/^::ffff:/i, "");
  if (!s || s === "::1" || s === "127.0.0.1" || s.toLowerCase() === "localhost") {
    return "";
  }
  return s;
}

function resolveCountryIsoCode(country) {
  if (!country) return "";
  const raw = String(country).trim();
  if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();
  return "";
}

function resolveCountryLabel(country, region) {
  const code = resolveCountryIsoCode(country);
  let name = "";
  if (code) {
    try {
      const regionNames = new Intl.DisplayNames(["en"], { type: "region" });
      name = regionNames.of(code) || countryCodeToName(code) || code;
    } catch {
      name = countryCodeToName(code) || code;
    }
  } else if (country) {
    name = String(country).trim();
  }
  const regionLabel = region ? String(region).trim() : "";
  if (name && regionLabel) return `${name}, ${regionLabel}`;
  return name || regionLabel || "";
}

function getPlatformIconSrc(deviceType, osName, osType) {
  const os = `${osName || ""} ${osType || ""}`.toLowerCase();
  const type = String(deviceType || "").toLowerCase();
  if (os.includes("ios") || os.includes("iphone") || os.includes("ipad")) return iosIcon;
  if (os.includes("android")) return androidIcon;
  if (os.includes("mac") || os.includes("osx")) return macIcon;
  if (os.includes("windows") || os.includes("win")) return windowsIcon;
  if (type.includes("personal") || type.includes("mobile") || type.includes("phone")) {
    return androidIcon;
  }
  return windowsIcon;
}

function getBrowserIconSrc(browserType) {
  const b = String(browserType || "").toLowerCase();
  if (b.includes("chrome")) return chromeIcon;
  if (b.includes("safari")) return safariIcon;
  if (b.includes("firefox")) return firefoxIcon;
  if (b.includes("edge")) return edgeIcon;
  return chromeIcon;
}

function getBrowserLabel(browserType) {
  const b = String(browserType || "").toLowerCase();
  if (b.includes("chrome")) return "Chrome";
  if (b.includes("safari")) return "Safari";
  if (b.includes("firefox")) return "Firefox";
  if (b.includes("edge")) return "Edge";
  if (b.includes("opera")) return "Opera";
  if (browserType) {
    return String(browserType).charAt(0).toUpperCase() + String(browserType).slice(1);
  }
  return "";
}

/** Legacy fs-device-os: icon only, centered */
export function AdminSigninDeviceCell({ deviceType, osName, osType }) {
  const iconSrc = getPlatformIconSrc(deviceType, osName, osType);
  return (
    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-600 border border-gray-200">
      <img
        src={iconSrc}
        alt={osName || osType || deviceType || "device"}
        className="w-5 h-5 object-contain"
        title={osName || osType || deviceType || "device"}
      />
    </span>
  );
}

/** Legacy fs-device-browser: icon + browser name (no version in label) */
export function AdminSigninBrowserCell({ browserType, browserVersion }) {
  const label = getBrowserLabel(browserType);
  if (!label) return <span className="text-muted-foreground">—</span>;
  const title = browserVersion ? `${label} ${browserVersion}` : label;
  return (
    <span className="inline-flex items-center gap-1">
      <img
        src={getBrowserIconSrc(browserType)}
        alt={label}
        className="w-6 h-6 object-contain shrink-0"
        title={title}
      />
      <span>{label}</span>
    </span>
  );
}

/** Build maps URL: prefer coordinates, then IP, then location text (legacy fs-ip behavior + IP fallback). */
function buildLocationMapsHref({ displayIp, lat, lng, countryLabel, city, region }) {
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`;
  }
  if (displayIp) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(displayIp)}`;
  }
  const text = [city, region, countryLabel].filter(Boolean).join(", ");
  if (text) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(text)}`;
  }
  return "";
}

/** Legacy fs-ip: IP on first line; flag + country (, region) below; maps link when lat/lng */
export function AdminSigninLocationCell({
  ipAddress,
  country,
  region,
  city,
  locationLat,
  locationLng,
}) {
  const displayIp = adminDisplayableIp(ipAddress);
  const lat = adminLocationCoord(locationLat);
  const lng = adminLocationCoord(locationLng);
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
  const countryCode = resolveCountryIsoCode(country);
  const countryLabel = resolveCountryLabel(country, region);

  if (!displayIp && !countryLabel && !hasCoords) {
    return <span className="text-muted-foreground">—</span>;
  }

  const mapsHref = buildLocationMapsHref({
    displayIp,
    lat,
    lng,
    countryLabel,
    city,
    region,
  });

  const countryContent = countryLabel ? (
    <span className="inline-flex items-center gap-1">
      {countryCode ? (
        <ReactCountryFlag
          countryCode={countryCode}
          svg
          aria-hidden="true"
          className="shrink-0"
          style={{ width: "1.1em", height: "0.85em" }}
          title={countryLabel}
        />
      ) : null}
      <span>{countryLabel}</span>
    </span>
  ) : null;

  const linkClass = "text-sm text-[#DC1F00] hover:underline";

  return (
    <div className="flex flex-col leading-snug">
      {displayIp ? (
        mapsHref ? (
          <a
            href={mapsHref}
            target="_blank"
            rel="noreferrer"
            className={linkClass}
            title="Open in Google Maps"
          >
            {displayIp}
          </a>
        ) : (
          <div className="text-sm text-gray-900">{displayIp}</div>
        )
      ) : null}
      {countryContent ? (
        <small className="text-xs text-gray-700">
          {!displayIp && mapsHref ? (
            <a
              href={mapsHref}
              target="_blank"
              rel="noreferrer"
              className={linkClass}
              title="Open in Google Maps"
            >
              {countryContent}
            </a>
          ) : (
            countryContent
          )}
        </small>
      ) : !displayIp && hasCoords && mapsHref ? (
        <small className="text-xs">
          <a
            href={mapsHref}
            target="_blank"
            rel="noreferrer"
            className={linkClass}
            title="Open in Google Maps"
          >
            {`${lat.toFixed(4)}, ${lng.toFixed(4)}`}
          </a>
        </small>
      ) : null}
    </div>
  );
}
